import { createContext, useContext, useState, useCallback, ReactNode } from 'react';

interface PageTitleContextValue {
  /** The current page title as a plain string (extracted from ReactNode) */
  title: string;
  /** The page icon name (HappyIcons name, e.g. from ICONS constants) */
  icon?: string;
  /** Whether the page header has been scrolled past (minimized) */
  isMinimized: boolean;
  /** Called by PageHeader to register its title and icon */
  setPageTitle: (title: string, icon?: string) => void;
  /** Called by PageHeader on scroll state change */
  setMinimized: (minimized: boolean) => void;
}

const PageTitleContext = createContext<PageTitleContextValue | null>(null);

export const PageTitleProvider = ({ children }: { children: ReactNode }) => {
  const [title, setTitle] = useState('');
  const [icon, setIcon] = useState<string | undefined>();
  const [isMinimized, setIsMinimized] = useState(false);

  const setPageTitle = useCallback((newTitle: string, newIcon?: string) => {
    setTitle(newTitle);
    setIcon(newIcon);
  }, []);

  const setMinimized = useCallback((minimized: boolean) => {
    setIsMinimized(minimized);
  }, []);

  return (
    <PageTitleContext.Provider value={{ title, icon, isMinimized, setPageTitle, setMinimized }}>
      {children}
    </PageTitleContext.Provider>
  );
};

export const usePageTitle = (): PageTitleContextValue => {
  const ctx = useContext(PageTitleContext);
  if (!ctx) {
    // Safe no-op fallback when used outside provider
    return {
      title: '',
      isMinimized: false,
      setPageTitle: () => {},
      setMinimized: () => {},
    };
  }
  return ctx;
};
